import { useState } from 'react';
import styled from '@emotion/styled';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getLocationFor } from '@trinity/chat-components/utils';
import { FileIcon } from '@trinity/chat-components/components/icons/File/index.js';
import type { SegmentAggregatedDataAPI } from '@trinity/chat/types/segment-aggregated-data';
import { getSegmentCardCitations } from '../api/client';

type CitationSection = SegmentAggregatedDataAPI.GetCardCitations.CitationSection;

interface CardCitationsModalProps {
  open: boolean;
  onClose: () => void;
  segmentId: number;
  cardKey: string;
  cardLabel: string;
}

export function CardCitationsModal({
  open,
  onClose,
  segmentId,
  cardKey,
  cardLabel,
}: CardCitationsModalProps): React.JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['segment-card-citations', segmentId, cardKey],
    queryFn: () => getSegmentCardCitations(segmentId, cardKey),
    enabled: open,
  });

  const sections = data?.sections ?? [];

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {cardLabel} — Sources
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <LoadingContainer>
            <CircularProgress size={24} />
          </LoadingContainer>
        )}
        {!isLoading && sections.length === 0 && (
          <EmptyState>No source transcripts found for this card.</EmptyState>
        )}
        {!isLoading && sections.length > 0 && (
          <>
            <SectionChips>
              {sections.map((s) => (
                <Chip key={s.sectionKey} label={s.sectionTitle} size="small" variant="outlined" />
              ))}
            </SectionChips>
            <SectionsList>
              {sections.map((section) => (
                <CollapsibleSection key={section.sectionKey} section={section} />
              ))}
            </SectionsList>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CollapsibleSection({ section }: { section: CitationSection }): React.JSX.Element {
  const [open, setOpen] = useState(true);

  return (
    <SectionBlock>
      <SectionHeader onClick={() => setOpen(!open)}>
        <SectionTitle>{section.sectionTitle}</SectionTitle>
        <CountBadge>{section.transcripts.length}</CountBadge>
        <ExpandIcon $expanded={open}>
          <ExpandMoreIcon fontSize="small" />
        </ExpandIcon>
      </SectionHeader>
      {open && (
        <TranscriptList>
          {section.transcripts.map((t) => {
            const loc = getLocationFor.workspaces.transcript({
              transcriptId: t.transcriptId,
              tab: 'extracted-data',
              section: section.sectionKey,
            });
            return (
              <TranscriptRow
                key={t.transcriptId}
                to={{ pathname: loc.pathname, search: loc.search }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <TranscriptRowIcon>
                  <FileIcon extension="txt" size={20} />
                </TranscriptRowIcon>
                <TranscriptRowName>{t.transcriptName}</TranscriptRowName>
              </TranscriptRow>
            );
          })}
        </TranscriptList>
      )}
    </SectionBlock>
  );
}

// ─── Styled components ──────────────────────────────────────────────────────

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const EmptyState = styled.div`
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
  padding: 24px 0;
`;

const SectionChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
`;

const SectionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionBlock = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
  background: rgba(0, 0, 0, 0.02);

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const SectionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--black-l, #323232);
  flex: 1;
`;

const CountBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  background: rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  padding: 1px 8px;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.4);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  display: flex;
`;

const TranscriptList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #f2f2f2;
  background: #ffffff;
`;

const TranscriptRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  cursor: pointer;
  box-sizing: border-box;
  border-bottom: 1px solid #f2f2f2;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.12s ease;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8faff;
  }

  &:focus-visible {
    outline: 2px solid #1271c4;
    outline-offset: -2px;
  }
`;

const TranscriptRowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`;

const TranscriptRowName = styled.div`
  color: #323232;
  font-size: 12px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
  flex: 1;
`;
